import { AppConfirmDialog, AppCopyText, AppPageHeader, AppSectionErrorBoundary, AppUseIcon, AppUserTypeChip } from 'components/ui';
import { SingleUserType, UserTypes } from 'types/GlobalTypes';
import { appTableFiltersInstance, usersTableColumns } from './UsersTableColumns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { AddUserDialog } from './AddUserDialog';
import { AppButton } from 'components/form';
import { IOrganization } from 'types/models/OrganizationModels';
import { StaticTableWithFilters } from 'components/Tables/StaticTableWithFilters';
import { TableCell } from '@trackman/web-shared-components';
import { observer } from 'mobx-react-lite';
import { useStores } from 'index';
import { useTranslation } from 'react-i18next';

export const UsersPage = observer(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { organizationsStore, usersStore, configurationService, startupStore } = useStores();
  const [isConfirmRevokeDialogOpen, setIsConfirmRevokeDialogOpen] = useState(false);
  const [isUsersDialogOpened, setIsUsersDialogOpened] = useState(false);
  const [userToRevoke, setUserToRevoke] = useState<SingleUserType | null>(null);
  const [organizationData, setOrganizationData] = useState<IOrganization | null>(null);
  const [invitePortalUrl, setInvitePortalUrl] = useState<string>('');

  useEffect(
    function buildInvitePortalUrl() {
      (async () => {
        const config = await configurationService.getConfigs();
        const baseUrl = config.invitePortalBaseUrl;
        setInvitePortalUrl(`${baseUrl}/Invites/${organizationId}`);
      })();
    },
    [organizationId]
  );

  useEffect(
    function fetchOrganizationUsers() {
      usersStore.getUsers(organizationId);
      handleGettingSingleOrganizationData();
    },
    [organizationId]
  );

  const handleGettingSingleOrganizationData = useCallback(async () => {
    const orgData = await organizationsStore.getSingleOrganization(organizationId);
    setOrganizationData(orgData || null);
  }, []);

  const rows = useMemo(() => usersStore.users, [usersStore.users]);

  const columns = useMemo(() => usersTableColumns(usersStore), []);

  const isValidGuid = (value: string): boolean => {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(value);
  };

  const handleRevokingUserAccess = useCallback(() => {
    if (!userToRevoke) return;
    usersStore.RevokeUser({ userId: userToRevoke.id, organizationId: organizationId }, () => setIsConfirmRevokeDialogOpen(false));
  }, [userToRevoke]);

  const handleRevokeWarning = useCallback(setUserToRevoke, []);

  const handleCancelRevokeUser = useCallback(() => setIsConfirmRevokeDialogOpen(false), []);

  useEffect(() => {
    if (!isConfirmRevokeDialogOpen) setTimeout(() => setUserToRevoke(null), 100);
  }, [isConfirmRevokeDialogOpen]);

  useEffect(() => {
    if (userToRevoke) setIsConfirmRevokeDialogOpen(true);
  }, [userToRevoke]);

  const handleClosingAddUserDialog = useCallback(() => {
    setIsUsersDialogOpened(false);
  }, []);

  const handleOpeningAddUserDialog = useCallback(() => {
    setIsUsersDialogOpened(true);
  }, []);

  useEffect(() => {
    if (!isUsersDialogOpened) usersStore.setSelectedUser(null);
  }, [isUsersDialogOpened]);

  const renderCell = useCallback(
    ({ cellValue, columnId, row }: { cellValue: any; columnHeader: string; columnId: string; row: any }) => {
      if (columnId === 'userId' || columnId === 'email') {
        return (
          <div className='d-flex justify-content-between align-items-center w-100'>
            <p data-test={`systemID-cell-text`} className='mb-0 overflow-ellipsis' title={cellValue}>
              {cellValue}
            </p>
            <AppCopyText className='ml-1 ' text={cellValue} />
          </div>
        );
      }

      if (columnId === 'type') {
        return <AppUserTypeChip userType={cellValue as UserTypes} />;
      }

      if (columnId === 'actions') {
        return (
          <>
            {
              <AppButton
                variant='secondary'
                className='border-secondary'
                data-test='revoke-user-button'
                onClick={() => handleRevokeWarning(row)}
                disabled={usersStore.isRevokingAccess}
              >
                {t('remove')}
                <AppUseIcon name='minusWithRoundedBorder' width='16' height='16' className='ml-2' color='#A0A0A0' />
              </AppButton>
            }
          </>
        );
      }

      return cellValue || '-';
    },
    [usersStore.isRevokingAccess]
  );

  const rowMapper = useCallback((cells: any[]) => {
    return (
      <>
        {cells.map(({ getCellProps, column, row, render, value }) => (
          <TableCell {...getCellProps()} align={column.align} onClick={() => {}}>
            {renderCell({ cellValue: value, columnHeader: column.Header, columnId: column.id, row: row.original })}
          </TableCell>
        ))}
      </>
    );
  }, []);

  return (
    <AppSectionErrorBoundary>
      <div className='users-tables'>
        <AppPageHeader
          title={`${t('users')}${organizationData ? ` ${t('in')} ${organizationData.name}` : ''}`}
          backBtn={{ isVisible: true, onClick: () => history.push('/') }}
        >
          <>
            <AppButton
              className='position-absolute right-0 mt-auto mb-auto'
              variant='transparent'
              disabled={usersStore.isLoadingUsers}
              data-test='add-user-button'
              id='add-user-button'
              onClick={handleOpeningAddUserDialog}
            >
              {t('addUser')}
              <AppUseIcon name='PlusIconWithoutBorder' width='14' height='14' className='ml-2' />
            </AppButton>
          </>
        </AppPageHeader>

        <div className='d-flex justify-content-end align-items-center mb-3'>
          <p className='color-textElementGrey font-13 mb-0'>
            <span className='mx-2'>{t('to_browse_invitations_navigate_to_invitations_portal')}</span>
            <a href={invitePortalUrl} target='_blank' rel='noreferrer' className='color-textElementGrey text-underline font-600'>
              {t('invitations')}
              <AppUseIcon name='ExternalLinkIcon' height='11' width='11' className='ml-1' />
            </a>
          </p>
        </div>

        {usersStore.isLoadingUsers ? (
          <StaticTableWithFilters<SingleUserType>
            appTableFiltersInstance={appTableFiltersInstance}
            rowMapper={rowMapper}
            columns={columns}
            data={[]}
            isLoading={true}
          />
        ) : usersStore.isOrgNotSynced ? (
          <div className='card' style={{ padding: '40px', textAlign: 'center' }}>
            <p className='mb-0'>
              {t('organizationNotSynchronized', {
                organizationName: organizationData?.name || organizationId,
                organizationId: organizationId
              })}
            </p>
          </div>
        ) : usersStore.usersLoadError ? (
          <div className='card' style={{ padding: '40px', textAlign: 'center' }}>
            <p className='mb-0'>
              {!isValidGuid(organizationId)
                ? t('invalidOrganizationId', { organizationId })
                : startupStore.isDevelopmentPortal()
                  ? t('failedToLoadUsersDevEnvironment')
                  : t('failedToLoadUsers')}
            </p>
          </div>
        ) : (
          <StaticTableWithFilters<SingleUserType>
            appTableFiltersInstance={appTableFiltersInstance}
            rowMapper={rowMapper}
            columns={columns}
            data={rows}
            isLoading={false}
          />
        )}

        {organizationData && (
          <AddUserDialog
            organizationData={organizationData}
            isOpened={isUsersDialogOpened}
            user={usersStore.selectedUser}
            onClose={handleClosingAddUserDialog}
          />
        )}

        <AppConfirmDialog
          isDialogOpen={isConfirmRevokeDialogOpen}
          isLoading={usersStore.isRevokingAccess}
          onConfirm={handleRevokingUserAccess}
          onCancel={handleCancelRevokeUser}
          title={`${t('removeUser')}`}
          message={`${t('removeUserConfirmation')}${userToRevoke?.email ? ' (' + userToRevoke?.email + ')' : ''}?`}
        />
      </div>
    </AppSectionErrorBoundary>
  );
});

export default UsersPage;
